import { z } from 'zod';

import { PAYMENT_GATE_TYPES } from '@/typings/api-models';

const assignToDataSchema = z.object({
  personType: z.enum(['context', 'team-member', 'customer', 'workflow-owner']),
  referenceId: z.string().nonempty(),
});

/**
 * Zod schema for a single payment gate config. Mirrors the backend
 * `PaymentGateConfigSchema` in noodle-api (CORE-1280 / CORE-1314 /
 * CORE-1315) so the UI validates the same shape the server will accept.
 * Backend field-level errors on save are surfaced inline via `setError`
 * from react-hook-form.
 *
 * `displayConditionExpressionId` and `assignTo` are carried on the schema
 * so the full config shape validates end-to-end, but they are not
 * user-editable in this modal — they flow in from the paired message
 * attachment's condition and assignment widgets at save time.
 */
export const paymentGateConfigSchema = z
  .object({
    invoiceTemplateRef: z.string().nonempty('Required'),
    gateType: z.enum([PAYMENT_GATE_TYPES.MINIMUM, PAYMENT_GATE_TYPES.PERCENTAGE], {
      errorMap: () => ({ message: 'Required' }),
    }),
    /** Cents. Present when gateType === 'minimum'. */
    minimumAmount: z.number().int().positive().optional().nullable(),
    /** 1–100. Present when gateType === 'percentage'. */
    minimumPercentage: z.number().int().min(1).max(100).optional().nullable(),
    displayConditionExpressionId: z.string().optional().nullable(),
    assignTo: z.array(assignToDataSchema).optional(),
  })
  .superRefine((value, ctx) => {
    if (value.gateType === PAYMENT_GATE_TYPES.MINIMUM) {
      if (typeof value.minimumAmount !== 'number') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Required',
          path: ['minimumAmount'],
        });
      }
      if (typeof value.minimumPercentage === 'number') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Leave blank when gate type is minimum',
          path: ['minimumPercentage'],
        });
      }
    }
    if (value.gateType === PAYMENT_GATE_TYPES.PERCENTAGE) {
      if (typeof value.minimumPercentage !== 'number') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Required',
          path: ['minimumPercentage'],
        });
      }
      if (typeof value.minimumAmount === 'number') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Leave blank when gate type is percentage',
          path: ['minimumAmount'],
        });
      }
    }
  });

/** Form field shape — nullable pass-throughs used by RHF. Transform before submit. */
export type PaymentGateFormValues = z.input<typeof paymentGateConfigSchema>;

/** Post-validation shape that matches the API contract. */
export type PaymentGateSubmitValues = z.output<typeof paymentGateConfigSchema>;
